import React, { FC, useMemo, useState } from 'react';
import { Button, Checkbox, Divider, Form, Message } from 'semantic-ui-react';

import { Uuid } from '@src/types';
import { useCurrentUser, useSystemToasts } from '@src/hooks';
import { useCentralCalls } from '@src/rest/CentralCalls';
import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { Align, Size } from '@src/types';
import { StyledContainer } from '@src/views/containers';

interface ImportSmartsheetDialogProps {
  projectId: Uuid;
  projectVersionId: Uuid;
  onComplete?: (result: any) => void;
  onClose?: () => void;
}

const ImportSmartsheetDialog: FC<ImportSmartsheetDialogProps> = ({
  projectId,
  projectVersionId,
  onComplete,
  onClose,
}) => {
  const { currentUser } = useCurrentUser();
  const { importSmartsheet } = useCentralCalls();
  const toastActions = useSystemToasts();

  const [sheetId, setSheetId] = useState('');
  const [smartsheetToken, setSmartsheetToken] = useState('');
  const [includeContainerRows, setIncludeContainerRows] = useState(false);
  const [newProjectVersionName, setNewProjectVersionName] = useState('');
  const [newProjectVersionDescription, setNewProjectVersionDescription] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return (
      !isSubmitting
      && sheetId.trim().length > 0
      && smartsheetToken.trim().length > 0
    );
  }, [isSubmitting, sheetId, smartsheetToken]);

  const handleSubmit = async () => {
    const accountId = currentUser?.id as string;

    if (!accountId) {
      setError('Current user account id is missing. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await importSmartsheet({
        SmartsheetToken: smartsheetToken.trim(),
        SheetId: sheetId.trim(),
        ProjectId: String(projectId),
        ProjectVersionId: String(projectVersionId),
        AccountId: accountId,
        IncludeContainerRows: includeContainerRows,
        NewProjectVersionName: newProjectVersionName.trim() || undefined,
        NewProjectVersionDescription: newProjectVersionDescription.trim() || undefined,
      });

      toastActions.success('Smartsheet import completed', 'Data has been imported from Smartsheet.');
      onComplete?.(result);
    } catch (ex) {
      const message = (ex as Error)?.message || 'Smartsheet import failed.';
      setError(message);
      toastActions.error('Smartsheet import failed', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BasicDialogContainer
      title="Import Smartsheet"
      roundedTop
      contentPadding={[Size.MD, Size.MD, Size.LG, Size.MD]}
      onClose={onClose}
    >
      <Form>
        <Form.Field required>
          <label>Smartsheet Sheet ID</label>
          <Form.Input
            placeholder="e.g. 1234567890123456"
            value={sheetId}
            onChange={(_, data) => setSheetId(data.value)}
            disabled={isSubmitting}
          />
        </Form.Field>

        <Form.Field required>
          <label>Smartsheet API Key</label>
          <Form.Input
            type="password"
            placeholder="Smartsheet API token"
            value={smartsheetToken}
            onChange={(_, data) => setSmartsheetToken(data.value)}
            disabled={isSubmitting}
          />
        </Form.Field>

        <Form.Field>
          <Checkbox
            label="Include container rows"
            checked={includeContainerRows}
            onChange={(_, data) => setIncludeContainerRows(Boolean(data.checked))}
            disabled={isSubmitting}
          />
        </Form.Field>

        <Divider />

        <Form.Field>
          <label>New Project Version Name (optional)</label>
          <Form.Input
            placeholder="Leave blank to import into current version"
            value={newProjectVersionName}
            onChange={(_, data) => setNewProjectVersionName(data.value)}
            disabled={isSubmitting}
          />
        </Form.Field>

        <Form.Field>
          <label>New Project Version Description (optional)</label>
          <Form.TextArea
            placeholder="Optional description for the new version"
            value={newProjectVersionDescription}
            onChange={(_, data) => setNewProjectVersionDescription(String(data.value ?? ''))}
            disabled={isSubmitting}
            rows={3}
          />
        </Form.Field>

        {error && (
          <Message negative>
            <Message.Content>{error}</Message.Content>
          </Message>
        )}
      </Form>

      <Divider />

      <StyledContainer textAlign={Align.RIGHT}>
        <Button
          type="button"
          basic
          content="Cancel"
          onClick={onClose}
          disabled={isSubmitting}
        />
        <Button
          type="button"
          color="purple"
          content={isSubmitting ? 'Importing...' : 'Import Smartsheet'}
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!canSubmit}
        />
      </StyledContainer>
    </BasicDialogContainer>
  );
};

export { ImportSmartsheetDialog };
